import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { Formik, Form, FastField, FieldProps } from "formik";
import * as Yup from "yup";
import { updateInboundPrice, ManualPriceUpdateDto } from "../../api/stockmoves/index.ts";
import { getCurrencyList } from "../../api/common/currencies.js";
import { getInboundItemsByInboundId, InboundItemDto } from "../../api/inbounditems/index.ts";

interface InboundPriceUpdateFormProps {
  props?: any;
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface Currency {
  id: number;
  name: string;
  symbol: string;
}

interface ProductPriceUpdate {
  productId: number;
  productName: string;
  productCode: string;
  unitCost: number;
  currencyId: number;
  quantityDelivered: number;
}

interface FormValues {
  productUpdates: ProductPriceUpdate[];
}

// Yup validation schema
const validationSchema = Yup.object().shape({
  productUpdates: Yup.array().of(
    Yup.object().shape({
      unitCost: Yup.number()
        .min(0, "El costo unitario debe ser mayor o igual a 0")
        .required("El costo unitario es requerido"),
      currencyId: Yup.number()
        .required("La moneda es requerida"),
    })
  ),
});

const InboundPriceUpdateForm: React.FC<InboundPriceUpdateFormProps> = ({
  props,
  onSuccess,
  onCancel,
}) => {
  const {id} = props || {};
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [initialValues, setInitialValues] = useState<FormValues>({
    productUpdates: []
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setInitialLoading(true);
        
        // Fetch currencies and inbound items in parallel
        const [currencyList, inboundItems] = await Promise.all([
          getCurrencyList(),
          getInboundItemsByInboundId(id)
        ]);
        
        setCurrencies(currencyList.data);
        
        // Initialize product updates from inbound items
        const updates: ProductPriceUpdate[] = inboundItems
          .filter(item => item.product) // Only items with products
          .map(item => ({
            productId: item.product!.id,
            productName: item.product!.name,
            productCode: item.product!.code,
            unitCost: 0,
            currencyId: currencyList.data.length > 0 ? currencyList.data[1].id : 0,
            quantityDelivered: item.quantityDelivered
          }));
        
        setInitialValues({
          productUpdates: updates
        });
      } catch (err) {
        console.error("Error al cargar los datos:", err);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleSubmit = async (values: FormValues, { setSubmitting, setStatus }: any) => {
    try {
      // Update each product's price individually
      for (const productUpdate of values.productUpdates) {
        if (productUpdate.unitCost > 0) {
          const updateData: ManualPriceUpdateDto = {
            inboundId: id,
            productId: productUpdate.productId,
            unitCost: productUpdate.unitCost,
            currencyId: productUpdate.currencyId,
          };

          await updateInboundPrice(updateData);
        }
      }
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setStatus("Error al actualizar los precios");
    } finally {
      setSubmitting(false);
    }
  };

  if (initialLoading) {
    return (
      <Box p={3} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Cargando productos...</Typography>
      </Box>
    );
  }

  if (initialValues.productUpdates.length === 0) {
    return (
      <Box p={3}>
        <Alert severity="info">
          No hay productos en esta entrada para actualizar precios.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h6" gutterBottom>
        Actualizar Precios de Productos
      </Typography>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting, status, values }) => (
          <Form>
            {status && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {status}
              </Alert>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {values.productUpdates.map((product, index) => (
                <Card key={product.productId} variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {product.productName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Código: {product.productCode} | Cantidad: {product.quantityDelivered}
                    </Typography>
                    
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <FastField name={`productUpdates.${index}.unitCost`}>
                        {({ field, meta }: FieldProps) => (
                          <TextField
                            {...field}
                            label="Costo Unitario"
                            type="number"
                            inputProps={{ step: "0.01", min: "0" }}
                            sx={{ flex: 1 }}
                            error={meta.touched && !!meta.error}
                            helperText={meta.touched && meta.error}
                            value={field.value || ''}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              field.onChange({
                                target: { name: field.name, value }
                              });
                            }}
                          />
                        )}
                      </FastField>

                      <FastField name={`productUpdates.${index}.currencyId`}>
                        {({ field, meta }: FieldProps) => (
                          <FormControl sx={{ minWidth: 200 }} error={meta.touched && !!meta.error}>
                            <InputLabel>Moneda</InputLabel>
                            <Select
                              {...field}
                              label="Moneda"
                              value={field.value || ''}
                            >
                              {currencies.map((currency) => (
                                <MenuItem key={currency.id} value={currency.id}>
                                  {currency.symbol} - {currency.name}
                                </MenuItem>
                              ))}
                            </Select>
                            {meta.touched && meta.error && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                {meta.error}
                              </Typography>
                            )}
                          </FormControl>
                        )}
                      </FastField>
                    </Box>
                  </CardContent>
                </Card>
              ))}

              <Divider />

              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                {onCancel && (
                  <Button variant="outlined" onClick={onCancel}>
                    Cancelar
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  startIcon={isSubmitting && <CircularProgress size={20} />}
                >
                  {isSubmitting ? "Actualizando..." : "Actualizar Precios"}
                </Button>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default InboundPriceUpdateForm;
